import React from "react";
import { useNavigate } from "react-router-dom";
import UElogo from "../assets/UElogo_horizontal.png";
import { ArrowBigLeft } from "lucide-react";
import { UserRoundCheck } from "lucide-react";
import { Check } from "lucide-react";
import { X } from 'lucide-react';

export default function TermsOfUsePage() {
  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };
  const goToContact = () => {
    navigate("/contact");
  };

  return (
    <div className="flex flex-col p-6 pt-28 w-full justify-start min-h-screen bg-gray-800 text-gray-100 p-8">
      {/* Top bar with logo and buttons */}
      <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
        {/* Logo on the left */}
        <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />

        {/* Buttons on the right */}
        <div className="flex gap-4">
          <button
            onClick={goBack}
            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded"
          >
            <ArrowBigLeft className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <h1 className="w-full mb-8 text-left px-6 text-xl font-bold">
        Terms of Use
      </h1>

      <p className="text-gray-300 mb-4">
        By using this reservation system, you agree that your name could be
        visible to other users when you book a space. This is necessary to
        ensure transparency and accountability in shared environments.
      </p>

      <p className="text-gray-300 mb-4">
        Only members of the University of Évora with a valid institutional email
        address are allowed to access and use the platform.{" "}
      </p>

      <p className="text-gray-300 mb-4">
        Faculty and staff of the University of Évora with a valid
        institutional email address can make reservations on Mateus d'Aranda and Complexo dos Leões.
      </p>

      <p className="text-gray-300 mb-4">
        Reservation of some rooms is subject to prior authorization.
      </p>
      
      <p className="text-gray-300 mb-4">
        For all users, weekend reservations must be made before 13:00h on the previous Friday.
      </p>

      <p className="text-gray-300 mb-4">
        Only Music students of the University of Évora with a valid
        institutional email address can make reservations on Mateus d'Aranda.
        Other students are not allowed to make reservations.
      </p>

      <p className="text-gray-300 text-lg font-bold mb-2 pl-4">
        Student Reservations
      </p>

      <p className="mb-2 pl-4">
        As a Music student, when using the reservation system, you agree to
        comply with the following rules and limitations:
      </p>

      <p className="font-semibold mt-4 pl-8">a) Permitted Use</p>
      <p className="pl-12">
        Student reservations are strictly intended for{" "}
        <strong>instrument practice</strong> and <strong>academic study</strong>{" "}
        purposes only.{" "}
      </p>

      <p className="font-semibold mt-4 pl-8">b) Classroom Reservation</p>
      <p className="mb-2 pl-12">
        Students can only reserve <strong>one room per time slot</strong>.{" "}
      </p>

      <p className="font-semibold mt-4 pl-8">c) Daily Reservations</p>
      <p className="pl-12">
        - Reservations may be made starting at 22:00h on the preceding day.{" "}
      </p>
      <p className="pl-12">
        - Piano and percussion students may reserve rooms equipped with and drums/percussion starting at 20:00h on the preceding day.{" "}
      </p>
      <p className="pl-12">
        - Daily limit per student: up to 6 hours per day — max 2 hours in each period 
        (09:00–14:00h, 14:00–19:00h, 19:00–23:00h; non-consecutive slots allowed).{" "}
      </p>

      <p className="font-semibold mt-4 pl-8">d) Eligible Classrooms</p>
      <p className="flex items-center flex-wrap gap-1 pl-12">
        Students may only reserve classrooms that are marked with
        <UserRoundCheck className="w-5 h-5 text-green-600 inline-block" />
        and the schedule is{" "}
        <span className="inline-flex h-5 w-5 items-center justify-center bg-green-600 rounded">
          <Check size={12} strokeWidth={7} className="text-white" /> </span>
        or <span className="inline-flex h-5 w-5 items-center justify-center bg-purple-600 rounded">
          <Check size={12} strokeWidth={7} className="text-white" /> </span>. 
        These indicate that the classroom is available.
      </p>


      <p className="text-gray-300 mt-4"> The marks stand for: </p>
      <p className="text-gray-300 pl-8"> - available <span className="inline-flex h-5 w-5 items-center justify-center bg-green-600 rounded">
        <Check size={12} strokeWidth={7} className="text-white" /> </span> . </p>
      <p className="text-gray-300 pl-8"> - reserved <span className="inline-flex h-5 w-5 items-center justify-center bg-red-600 rounded">
        <X size={12} strokeWidth={7} className="text-white" /> </span> . </p>
      <p className="text-gray-300 pl-8"> - reserved for classes <span className="inline-flex h-5 w-5 items-center justify-center bg-yellow-600 rounded">
        <X size={12} strokeWidth={7} className="text-white" /> </span> . </p>
      <p className="text-gray-300 pl-8"> - requested approval for reservation <span className="inline-flex h-5 w-5 items-center justify-center bg-blue-600 rounded">
        <X size={12} strokeWidth={7} className="text-white" /> </span> . </p>
      <p className="text-gray-300 pl-8"> - unavailable <span className="inline-flex h-5 w-5 items-center justify-center bg-gray-700 rounded">
        <X size={12} strokeWidth={7} className="text-white" /> </span> . </p>
      <p className="text-gray-300 pl-8"> - Admin-canceled — available again <span className="inline-flex h-5 w-5 items-center justify-center bg-purple-600 rounded">
        <Check size={12} strokeWidth={7} className="text-white" /> </span> . </p>

      <p className="text-gray-300 mt-4">
        Circumstances that may result in the temporary suspension of reservation privileges:
      </p>
      <p className="text-gray-300 pl-8">
        - misuse of the system, such as reserving a classroom without attending (fully or partially).{" "}
      </p>
      <p className="text-gray-300 pl-8">
        - improper use of the classroom’s equipment or unauthorized alteration of its furnishings or materials.{" "}
      </p>
      <p className="text-gray-300 pl-8">
        - failing to maintain the classroom in a clean condition.
      </p>
      <p> </p>



      <p className="text-gray-300 mt-4">
        The School of Arts only manages the online Space Reservation System and is not responsible for the physical spaces.
        Any inquiries regarding the spaces should be directed to the University of Évora.
      </p>

      <p className="text-gray-300 mt-6">
        If you have any questions or recommendations regarding reservations,
        please visit the Contact page to get in touch with us.
      </p>

      <p className="text-gray-300 text-lg font-bold mt-8 mb-2">
        Website Ownership & Rights
      </p>

      <p className="text-gray-300 mb-4">
        This website and the Space Reservation System (including its software code, design, layout, graphics, 
        and compiled assets) are the property of the <strong>current Direction of the School of Arts of the 
          University of Évora</strong>. All rights are reserved unless expressly granted in these Terms.
      </p>

      <p className="text-gray-300 font-semibold mt-4 pl-4">Limited License to Users</p>
      <ul className="list-disc pl-12 text-gray-300 space-y-2">
        <li>
          The School of Arts grants you a <strong>personal, non-exclusive, non-transferable, revocable</strong> license to access and use the site 
          solely for legitimate University purposes, in accordance with these Terms and applicable policies.
        </li>
        <li>
          You may not copy, modify, reverse-engineer, decompile, scrape, automate, resell, or otherwise exploit the website, its code, or any part of the service.
        </li>
      </ul>

      <p className="text-gray-300 font-semibold mt-4 pl-4">Administration & Control</p>
      <ul className="list-disc pl-12 text-gray-300 space-y-2">
        <li>
          The Direction may manage hosting, access control, integrations, analytics, and maintenance; and may  
           <strong>modify, suspend, or discontinue</strong> features or access (temporarily or permanently) when necessary.
        </li>
        <li>
          The Direction may update configurations, rules, and user interfaces without prior notice to ensure security, compliance, and fair use.
        </li>
      </ul>

      <p className="text-gray-300 font-semibold mt-4 pl-4">Trademarks & Branding</p>
      <ul className="list-disc pl-12 text-gray-300 space-y-2">
        <li>
          “Universidade de Évora”, “School of Arts”, and related names, logos, and marks are used under authorization. 
          No license to use these marks is granted to users.
        </li>
      </ul>

      <p className="text-gray-300 font-semibold mt-4 pl-4">User Contributions & Data</p>
      <ul className="list-disc pl-12 text-gray-300 space-y-2">
        <li>
          Content you submit (e.g., reservation details) remains yours, but you grant the School of Arts a 
          <strong> non-exclusive, royalty-free, worldwide</strong> license to use, store, process, and display it to operate the service and fulfill its purposes.
        </li>
        <li>
          The School of Arts may retain and process operational data/logs in line with University policies and applicable law.
        </li>
      </ul>

      <p className="text-gray-300 font-semibold mt-4 pl-4">Third-Party & Open-Source Components</p>
      <ul className="list-disc pl-12 text-gray-300 space-y-2">
        <li>
          The website may incorporate third-party and open-source software. Such components remain governed by their respective licenses.
        </li>
      </ul>

      <p className="text-gray-300 mt-4">
        © {new Date().getFullYear()} University of Évora — School of Arts. All rights reserved.
      </p>

      
      {/* Contact Link */}
      <div className="mt-auto flex gap-[1cm] justify-center">
        <button
          onClick={goToContact}
          className="mt-auto pt-[1cm] text-sm text-gray-400 text-center"
        >
          Contact
        </button>
      </div>
    </div>
  );
}
